import * as React from 'react';
import DivisionsAndTeamsQuery from 'core/gql/queries/divisions-and-teams';
import { Button, FieldMessage, Modal, ProgressBar, Select, Icon, Statuses } from '@teamsnap/teamsnap-ui';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import { useApolloClient } from '@apollo/client';
import { Member } from 'models';
import * as QueryPrograms from '../../../core/gql/queries/programs';
import MUTATION from '../../../core/gql/mutations/copy-member';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import formatDivisionsAndTeams, { sortByLabel } from '../formatDivisionsAndTeams';
import { displayLastNameFirst } from 'core/utils';
import { updateSeasonParticipantCache } from '../season-participants-service';

import './copy-profile-modal.scss';

const numberOfSimulataneousCopies = 1;

type Props = {
  orgId: number;
  members: Member[];
  show: boolean;
  contextSeasonId?: number;
  setShow: (x: boolean) => void;
  whenFinishCopy?: () => void;
};

interface BulkMembersStatus {
  totalMembers: number;
  processedMembersCount: number;
  membersWithErrors: Member[];
}

const defOption = { label: '' };

const roleOptions = [
  defOption,
  { value: 'player', label: 'Player' },
  { value: 'player_manager', label: 'Player & Manager' },
  { value: 'manager', label: 'Manager & Non-player' },
  { value: 'none', label: 'Non-player' },
];

const formatName = ({ firstName, lastName }) => {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }

  return firstName ? firstName : lastName;
};

const showFeedback = (success: boolean) => {
  reactiveFeedbackMessage({
    message: success ? 'Participant copied to Team/Division' : 'Failed to copy participant. Try again',
    autoclose: success,
    isError: !success,
  });
};

const CopyParticipantProfileModal = ({ show, setShow, members, orgId, contextSeasonId, whenFinishCopy }: Props) => {
  const [selectedRole, setSelectedRole] = React.useState<string>();
  const [selectedDivision, setSelectedDivision] = React.useState<number>();
  const [fetchSeason, { data: seasonData }] = useLazyQuery(DivisionsAndTeamsQuery);
  const [attemptedSubmit, setAttemptedSubmit] = React.useState(false);
  const [selectedSeason, setSelectedSeason] = React.useState<number>();
  const [selectedTeam, setSelectedTeam] = React.useState<string>();
  const [bulkMembersStatus, setBulkMembersStatus] = React.useState<BulkMembersStatus>();
  const [updatedMembers, setUpdatedMembers] = React.useState<Member[]>([]);
  const updatedMembersRef = React.useRef(updatedMembers);
  const client = useApolloClient();
  const [copyMember] = useMutation(MUTATION, {
    onCompleted({ copyMember: response }) {
      const newUpdatedMembers = updatedMembers.concat(response);
      updatedMembersRef.current = newUpdatedMembers;
      setUpdatedMembers(newUpdatedMembers);
    },
  });
  const [submitting, setSubmitting] = React.useState<boolean>();
  const [errorsCopied, setErrorsCopied] = React.useState(false);

  const { data: programsData } = useQuery(QueryPrograms.QUERY_WITH_SEASONS, {
    variables: { organizationId: orgId },
  });

  const [divisionsFilter, teamsFilter] = formatDivisionsAndTeams(selectedSeason ? seasonData : null, selectedDivision);

  // The test is not mocking the word "programs", that's why we need to use this ternary operator
  const programs = programsData?.programs;
  const seasonsFilter = programs
    ? [
        defOption,
        ...programs
          .flatMap(({ name, seasons }) => seasons.map((x) => ({ value: x.id, label: `${name} (${x.name})` })))
          .sort(sortByLabel),
      ]
    : [];

  const isBulkCopy = members?.length > 1;
  let heading = null;

  if (!isBulkCopy && members?.length === 1) {
    heading = `Copy ${formatName(members[0])}'s Profile`;
  } else if (submitting && isBulkCopy) {
    heading = 'Copying Profiles...';
  } else if (!submitting && bulkMembersStatus?.membersWithErrors.length > 0) {
    heading = (
      <div>
        <Icon name="alert" mods={'u-fontSizeLg u-colorHighlight u-spaceRightSm'} />
        Copy Incomplete
      </div>
    );
  } else if (isBulkCopy) {
    heading = 'Copy Profiles';
  } else {
    heading = 'Copy Complete';
  }

  React.useEffect(() => {
    if (selectedSeason) {
      fetchSeason({ variables: { seasonId: selectedSeason } });
    }
  }, [selectedSeason, fetchSeason]);

  const close = (event = null, clearSelected = false) => {
    setAttemptedSubmit(false);
    setShow(false);
    setSelectedRole(null);
    setSelectedDivision(null);
    setSelectedTeam(null);
    setBulkMembersStatus(null);
    clearSelected && whenFinishCopy && whenFinishCopy();
  };

  const copyMemberAction = async (memberToCopy) => {
    await copyMember({
      variables: {
        seasonId: selectedSeason,
        contextSeasonId: contextSeasonId && `${contextSeasonId}`,
        organizationId: orgId,
        divisionId: selectedDivision,
        memberId: parseInt(memberToCopy.id),
        teamId: selectedTeam === 'noTeam' ? undefined : parseInt(selectedTeam),
        isManager: selectedRole.includes('manager'),
        isPlayer: selectedRole.includes('player'),
      },
    });
  };

  const bulkCopyMemberAction = async (membersToCopy: Member[]) => {
    const bulkStatus = {
      totalMembers: membersToCopy.length,
      processedMembersCount: 0,
      membersWithErrors: [],
    };

    setBulkMembersStatus(bulkStatus);

    // clone so we can alter array to process in chunks
    const members = [...membersToCopy];
    const membersGroupToCopy = members.splice(0, numberOfSimulataneousCopies);

    await copyNextMembersGroup(membersGroupToCopy, members, bulkStatus);
  };

  const copyNextMembersGroup = async (
    membersGroupToCopy: Member[],
    remainingMembers: Member[],
    bulkJobStatus: BulkMembersStatus
  ) => {
    const results = await Promise.allSettled(
      membersGroupToCopy.map(async (memberToCopy: Member) => await copyMemberAction(memberToCopy))
    );

    let successfulCopies = 0;
    const membersWithErrors = [];

    // @ts-ignore added because it complains about doing forEach on the response
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        membersWithErrors.push(membersGroupToCopy[index]);
      } else {
        successfulCopies += 1;
      }
    });

    const newBulkJobStatus = {
      ...bulkJobStatus,
      processedMembersCount: bulkJobStatus.processedMembersCount + successfulCopies,
      membersWithErrors: bulkJobStatus.membersWithErrors.concat(membersWithErrors),
    };

    setBulkMembersStatus(newBulkJobStatus);

    if (remainingMembers.length > 0) {
      const nextMembersGroupToCopy = remainingMembers.splice(0, numberOfSimulataneousCopies);

      return copyNextMembersGroup(nextMembersGroupToCopy, remainingMembers, newBulkJobStatus);
    } else {
      return Promise.resolve();
    }
  };

  const onSubmit = async () => {
    setAttemptedSubmit(true);
    if (!selectedSeason || !selectedDivision || !selectedTeam || !selectedRole) return;

    if (!isBulkCopy) {
      const member = members[0];
      try {
        setSubmitting(true);
        await copyMemberAction(member);
        updateSeasonParticipantCache(updatedMembersRef.current);

        showFeedback(true);
        setSubmitting(false);
        close();
      } catch (e) {
        setSubmitting(false);
        showFeedback(false);
      }
    } else {
      setSubmitting(true);
      await bulkCopyMemberAction(members);
      updateSeasonParticipantCache(updatedMembersRef.current);

      setSubmitting(false);
    }
  };

  const copyProfilesResults = () => {
    const errorListValue = bulkMembersStatus.membersWithErrors.reduce(
      (acc, member) => acc + displayLastNameFirst(member) + '\n',
      ''
    );

    return (
      <div className="modal-container copy-profile-results">
        <div className="u-sizeFull">
          <ProgressBar progress={(bulkMembersStatus.processedMembersCount / bulkMembersStatus.totalMembers) * 100} />
          <div className="u-padTopSm">
            {`${bulkMembersStatus.processedMembersCount} of ${bulkMembersStatus.totalMembers} profiles copied`}
          </div>
        </div>

        {!submitting && bulkMembersStatus.membersWithErrors.length > 0 ? (
          <p className="u-textLeft u-padTopMd">
            We were unable to copy the following profiles due to an internal error. Please take note of these records
            and try again.
          </p>
        ) : null}

        <p className="u-textLeft">
          {bulkMembersStatus.membersWithErrors.map((member) => (
            <>
              <br />
              {displayLastNameFirst(member)}
            </>
          ))}
        </p>

        {submitting ? (
          <p className="u-textLeft u-padTopMd u-padBottomLg">
            Please do not close this window until progress is complete. This may take a minute. Leave this tab open and
            you can work on other things…
          </p>
        ) : null}

        {!submitting ? (
          <div className="buttons-wrapper">
            {bulkMembersStatus.membersWithErrors.length > 0 ? (
              <Button
                type="button"
                mods="u-spaceRightSm"
                onClick={() => {
                  const cb = navigator.clipboard;
                  cb.writeText(errorListValue).then(() => {
                    setErrorsCopied(true);
                    setTimeout(() => {
                      setErrorsCopied(false);
                    }, 1000);
                  });
                }}
              >
                <Icon
                  name={errorsCopied ? 'task-alt' : 'content-copy'}
                  style={{
                    right: 10,
                    color: errorsCopied ? '#199a50' : '#1D2B35',
                  }}
                />
                {errorsCopied ? 'Copied' : 'Copy Names'}
              </Button>
            ) : null}
            <Button mods="Button Button--primary" onClick={(e) => close(e, true)}>
              Done
            </Button>
          </div>
        ) : null}
      </div>
    );
  };

  const selectCopyParameters = () => (
    <div className="modal-container">
      <p style={{ textAlign: 'start' }}>
        Copy basic player and contact information into a new roster profile and add this new profile to another
        team/division.
      </p>

      <label htmlFor="season" className="FieldGroup-label u-spaceTopMd">
        Program
      </label>
      <Select
        name="season"
        options={seasonsFilter}
        inputProps={{
          value: selectedSeason || '',
          onChange: (e) => setSelectedSeason(parseInt(e.target.value)),
        }}
      />
      {attemptedSubmit && !selectedSeason && <FieldMessage status={Statuses.ERROR}>Select a program</FieldMessage>}

      <label htmlFor="division" className="FieldGroup-label u-spaceTopMd">
        Division
      </label>
      <Select
        name="division"
        disabled={!selectedSeason}
        options={[defOption, ...divisionsFilter]}
        inputProps={{
          value: selectedDivision || '',
          onChange: (e) => setSelectedDivision(parseInt(e.target.value)),
        }}
      />
      {attemptedSubmit && !selectedDivision && <FieldMessage status={Statuses.ERROR}>Select a division</FieldMessage>}

      <label htmlFor="team" className="FieldGroup-label u-spaceTopMd">
        Team
      </label>
      <Select
        name="team"
        disabled={!selectedDivision}
        options={[defOption, ...teamsFilter]}
        inputProps={{
          value: selectedTeam || '',
          onChange: (e) => setSelectedTeam(e.target.value),
        }}
      />
      {attemptedSubmit && !selectedTeam && <FieldMessage status={Statuses.ERROR}>Select a team</FieldMessage>}

      <label htmlFor="role" className="FieldGroup-label u-spaceTopMd">
        Role
      </label>
      <Select
        name="role"
        options={roleOptions}
        inputProps={{
          placeholder: 'role',
          value: selectedRole || '',
          onChange: (e) => setSelectedRole(e.target.value),
        }}
      />
      {attemptedSubmit && !selectedRole && <FieldMessage status={Statuses.ERROR}>Select a role</FieldMessage>}

      <div className="buttons-wrapper">
        <Button mods="u-spaceRightSm" onClick={close} isDisabled={submitting}>
          Cancel
        </Button>
        <Button testId="copy-profile-button" mods="Button Button--primary" onClick={onSubmit} isDisabled={submitting}>
          {isBulkCopy ? 'Copy Profiles' : 'Copy Profile'}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      heading={heading}
      style={{ minWidth: '0', width: '450px', textAlign: 'start' }}
      show={show}
      showClose={!submitting}
      closeFn={close}
    >
      {bulkMembersStatus ? copyProfilesResults() : selectCopyParameters()}
    </Modal>
  );
};

CopyParticipantProfileModal.defaultProps = {
  whenFinishCopy: null,
  contextSeasonId: null,
};

export default CopyParticipantProfileModal;
